import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import Logo from "../components/Logo";
import { toast } from "sonner";
import {
  Store,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Lock,
  Gift
} from "lucide-react";

// Onboarding steps (Preview is now a separate route: /onboarding/preview)
const STEPS = {
  CONNECT: 1,
  ANALYZING: 2
};

const Onboarding = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [currentStep, setCurrentStep] = useState(STEPS.CONNECT);
  const [shopDomain, setShopDomain] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [isAffiliate, setIsAffiliate] = useState(false);

  // Check affiliate status on mount
  useEffect(() => {
    const checkAffiliateStatus = async () => {
      try {
        const res = await api.get("/affiliates/me");
        setIsAffiliate(res.data?.is_affiliate === true);
      } catch (err) {
        setIsAffiliate(false);
      }
    };
    checkAffiliateStatus();
  }, []);

  // Define generateInstantInsight before useEffect hooks that use it
  const generateInstantInsight = useCallback(async (storeId) => {
    setAnalyzing(true);
    setAnalysisProgress(0);
    
    // Simulate progress updates for better UX
    const progressInterval = setInterval(() => {
      setAnalysisProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 800);
    
    try {
      // Call the instant insight endpoint (doesn't require subscription)
      const res = await api.post("/onboarding/instant-insight", { store_id: storeId });
      
      clearInterval(progressInterval);
      setAnalysisProgress(100);
      
      // Short delay to show 100% progress before navigating
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Navigate to preview page with insight data
      navigate("/onboarding/preview", { 
        state: { insightData: res.data },
        replace: true 
      });
    } catch (error) {
      clearInterval(progressInterval);
      console.error("Failed to generate insight:", error);
      const message = error.response?.data?.detail || "Failed to generate insight";
      toast.error(message);
      
      // Navigate to preview anyway (will show locked cards only)
      navigate("/onboarding/preview", { replace: true });
    } finally {
      setAnalyzing(false);
    }
  }, [navigate]);

  // Check health status on mount
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const res = await api.get("/health");
        setHealthStatus(res.data);
      } catch (error) {
        console.error("Health check failed:", error);
      }
    };
    checkHealth();
  }, []);

  // Check for returning from Shopify OAuth (store just connected)
  useEffect(() => {
    const storeConnected = searchParams.get("store_connected");
    const storeId = searchParams.get("store_id");
    
    if (storeConnected === "true" && storeId) {
      // Store the fact that user just connected a store (in case of session issues)
      // Use localStorage for persistence across page reloads/redirects
      localStorage.setItem('just_connected_store', storeId);
      localStorage.setItem('just_connected_store_time', Date.now().toString());
      
      // User just connected their store, start analysis
      setCurrentStep(STEPS.ANALYZING);
      generateInstantInsight(storeId);
    } else {
      // Check if we have a pending store connection from a previous session (within 5 minutes)
      const pendingStoreId = localStorage.getItem('just_connected_store');
      const pendingTime = localStorage.getItem('just_connected_store_time');
      
      if (pendingStoreId && pendingTime) {
        const elapsed = Date.now() - parseInt(pendingTime, 10);
        // Only use if within 5 minutes (300000ms)
        if (elapsed < 300000) {
          localStorage.removeItem('just_connected_store');
          localStorage.removeItem('just_connected_store_time');
          setCurrentStep(STEPS.ANALYZING);
          generateInstantInsight(pendingStoreId);
        } else {
          // Expired, clean up
          localStorage.removeItem('just_connected_store');
          localStorage.removeItem('just_connected_store_time');
        }
      }
    }
  }, [searchParams, generateInstantInsight]);

  // Check if user already has stores (redirect to dashboard)
  useEffect(() => {
    const checkStores = async () => {
      try {
        const res = await api.get("/shopify/stores");
        const stores = res.data?.stores || [];
        
        if (stores.length > 0) {
          // User has stores, check if they have a subscription
          const billingRes = await api.get("/billing/status");
          const status = billingRes.data?.status;
          
          if (status === "active" || status === "trialing" || status === "lifetime") {
            // Has subscription, go to dashboard
            navigate("/dashboard", { replace: true });
          } else {
            // Has store but no subscription - show preview
            setStoreInfo(stores[0]);
            setCurrentStep(STEPS.ANALYZING);
            generateInstantInsight(stores[0].store_id);
          }
        }
      } catch (error) {
        console.error("Failed to check stores:", error);
      }
    };
    
    if (user) {
      checkStores();
    }
  }, [user, navigate, generateInstantInsight]);

  const handleConnectStore = async () => {
    if (!shopDomain.trim()) {
      toast.error("Please enter your store domain");
      return;
    }

    if (!healthStatus?.shopify_configured) {
      toast.error("Store connection is temporarily unavailable. Please try again later.");
      return;
    }

    setConnecting(true);
    try {
      // Use onboarding-specific connect endpoint (no subscription required)
      const res = await api.get(`/onboarding/shopify-connect?shop=${encodeURIComponent(shopDomain)}`);
      
      // Log debug info for OAuth troubleshooting
      console.log('[Shopify OAuth] State created:', {
        state_prefix: res.data.state_nonce_prefix,
        debug_info: res.data.debug_info,
        oauth_url_preview: res.data.oauth_url?.substring(0, 100) + '...'
      });
      
      if (res.data.oauth_url) {
        // Store OAuth debug info before redirect
        localStorage.setItem('oauth_debug_state', JSON.stringify({
          state_prefix: res.data.state_nonce_prefix,
          shop: shopDomain,
          created_at: new Date().toISOString(),
          redirect_uri: res.data.debug_info?.redirect_uri
        }));
        
        window.location.href = res.data.oauth_url;
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string" ? detail : "Failed to connect store";
      toast.error(message);
    } finally {
      setConnecting(false);
    }
  };

  // Render Step 1: Connect Store
  const renderConnectStep = () => (
    <div className="max-w-xl mx-auto text-center">
      {/* PHASE 5: Clear 4-step indicator */}
      <div className="flex items-center justify-center gap-1 mb-8">
        <div className="flex flex-col items-center">
          <div className="w-8 h-8 rounded-full bg-[#D4F34A] text-black flex items-center justify-center font-bold text-sm">1</div>
          <span className="text-xs text-[#D4F34A] mt-1 hidden sm:block">Sign in</span>
        </div>
        <div className="w-8 sm:w-12 h-0.5 bg-[#D4F34A]"></div>
        <div className="flex flex-col items-center">
          <div className="w-8 h-8 rounded-full bg-[#D4F34A] text-black flex items-center justify-center font-bold text-sm">2</div>
          <span className="text-xs text-[#D4F34A] mt-1 hidden sm:block">Connect</span>
        </div>
        <div className="w-8 sm:w-12 h-0.5 bg-[#262626]"></div>
        <div className="flex flex-col items-center">
          <div className="w-8 h-8 rounded-full bg-[#262626] text-text-muted flex items-center justify-center font-bold text-sm">3</div>
          <span className="text-xs text-text-muted mt-1 hidden sm:block">Activate</span>
        </div>
        <div className="w-8 sm:w-12 h-0.5 bg-[#262626]"></div>
        <div className="flex flex-col items-center">
          <div className="w-8 h-8 rounded-full bg-[#262626] text-text-muted flex items-center justify-center font-bold text-sm">4</div>
          <span className="text-xs text-text-muted mt-1 hidden sm:block">Insights</span>
        </div>
      </div>

      <div className="w-20 h-20 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-6">
        <Store className="w-10 h-10 text-[#D4F34A]" />
      </div>
      
      <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
        Connect your Shopify store
      </h1>
      <p className="text-text-muted text-lg mb-8">
        We&apos;ll analyze your store data and show you exactly where you&apos;re losing money.
      </p>

      <Card className="bg-surface border-[#262626]">
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-left mb-2">
                Store URL
              </label>
              <Input
                type="text"
                placeholder="your-store.myshopify.com"
                value={shopDomain}
                onChange={(e) => setShopDomain(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleConnectStore()}
                className="bg-[#0a0a0a] border-[#262626] text-white placeholder:text-gray-500"
                data-testid="shopify-domain-input"
              />
              <p className="text-xs text-text-muted text-left mt-2">
                Enter your myshopify.com URL (e.g., &quot;mystore&quot; or &quot;mystore.myshopify.com&quot;)
              </p>
            </div>

            <Button
              onClick={handleConnectStore}
              disabled={connecting || !healthStatus?.shopify_configured}
              className="w-full bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold py-6 text-lg"
              data-testid="connect-store-btn"
            >
              {connecting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Connecting...
                </>
              ) : (
                <>
                  Connect Store
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>

          <div className="mt-6 pt-6 border-t border-[#262626]">
            <p className="text-xs text-text-muted">
              <Lock className="w-3 h-3 inline mr-1" />
              Read-only access • We never modify your store data
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Trust badges */}
      <div className="flex items-center justify-center gap-6 mt-8 text-xs text-text-muted">
        <span className="flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
          256-bit encryption
        </span>
        <span className="flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
          GDPR compliant
        </span>
        <span className="flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3 text-[#D4F34A]" />
          SOC 2 certified
        </span>
      </div>
    </div>
  );

  // Render Step 2: Analyzing
  const renderAnalyzingStep = () => (
    <div className="max-w-xl mx-auto text-center">
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2 mb-8">
        <div className="w-8 h-8 rounded-full bg-[#D4F34A] text-black flex items-center justify-center font-bold text-sm">
          <CheckCircle2 className="w-4 h-4" />
        </div>
        <div className="w-16 h-0.5 bg-[#D4F34A]"></div>
        <div className="w-8 h-8 rounded-full bg-[#D4F34A] text-black flex items-center justify-center font-bold text-sm">2</div>
        <div className="w-16 h-0.5 bg-[#262626]"></div>
        <div className="w-8 h-8 rounded-full bg-[#262626] text-text-muted flex items-center justify-center font-bold text-sm">3</div>
      </div>

      <div className="w-20 h-20 rounded-full bg-[#D4F34A]/10 flex items-center justify-center mx-auto mb-6">
        <Loader2 className="w-10 h-10 text-[#D4F34A] animate-spin" />
      </div>

      <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-4">
        Analyzing your store...
      </h1>
      <p className="text-text-muted text-lg mb-8">
        We&apos;re crunching your data to find profit opportunities. This takes about 30 seconds.
      </p>

      {/* Progress bar */}
      <div className="w-full bg-[#262626] rounded-full h-2 mb-6">
        <div 
          className="bg-[#D4F34A] h-2 rounded-full transition-all duration-500"
          style={{ width: `${Math.min(analysisProgress, 100)}%` }}
        ></div>
      </div>

      {/* Animated progress steps */}
      <div className="space-y-4">
        <div className={`flex items-center gap-3 text-left bg-surface border border-[#262626] rounded-lg px-4 py-3 ${analysisProgress > 0 ? 'opacity-100' : 'opacity-60'}`}>
          {analysisProgress >= 30 ? (
            <CheckCircle2 className="w-5 h-5 text-[#D4F34A] flex-shrink-0" />
          ) : (
            <Loader2 className="w-5 h-5 text-[#D4F34A] animate-spin flex-shrink-0" />
          )}
          <span className="text-sm">Fetching order data from Shopify...</span>
        </div>
        <div className={`flex items-center gap-3 text-left bg-surface border border-[#262626] rounded-lg px-4 py-3 ${analysisProgress >= 30 ? 'opacity-100' : 'opacity-60'}`}>
          {analysisProgress >= 60 ? (
            <CheckCircle2 className="w-5 h-5 text-[#D4F34A] flex-shrink-0" />
          ) : analysisProgress >= 30 ? (
            <Loader2 className="w-5 h-5 text-[#D4F34A] animate-spin flex-shrink-0" />
          ) : (
            <div className="w-5 h-5 rounded-full border-2 border-[#262626] flex-shrink-0"></div>
          )}
          <span className={`text-sm ${analysisProgress >= 30 ? 'text-white' : 'text-text-muted'}`}>Computing revenue metrics...</span>
        </div>
        <div className={`flex items-center gap-3 text-left bg-surface border border-[#262626] rounded-lg px-4 py-3 ${analysisProgress >= 60 ? 'opacity-100' : 'opacity-60'}`}>
          {analysisProgress >= 90 ? (
            <CheckCircle2 className="w-5 h-5 text-[#D4F34A] flex-shrink-0" />
          ) : analysisProgress >= 60 ? (
            <Loader2 className="w-5 h-5 text-[#D4F34A] animate-spin flex-shrink-0" />
          ) : (
            <div className="w-5 h-5 rounded-full border-2 border-[#262626] flex-shrink-0"></div>
          )}
          <span className={`text-sm ${analysisProgress >= 60 ? 'text-white' : 'text-text-muted'}`}>Identifying profit opportunities...</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Logo showText linkTo="/" />
          
          <div className="flex items-center gap-4">
            <span className="text-text-muted text-sm hidden md:block">{user?.email}</span>
            {/* Affiliate Dashboard Link - visible to affiliates */}
            {isAffiliate && (
              <Link to="/affiliates/dashboard">
                <Button variant="ghost" className="text-text-muted hover:text-white text-sm" data-testid="affiliate-dashboard-btn">
                  <Gift className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">Affiliate Dashboard</span>
                  <span className="sm:hidden">Affiliate</span>
                </Button>
              </Link>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-text-muted hover:text-white"
              onClick={logout}
              data-testid="logout-btn"
            >
              Sign out
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6 md:px-12">
        {currentStep === STEPS.CONNECT && renderConnectStep()}
        {currentStep === STEPS.ANALYZING && renderAnalyzingStep()}
      </main>
    </div>
  );
};

export default Onboarding;
